/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axiosClient';
import { useTheme } from '../../context/ThemeContext';
import { useBookingDraft } from '../../context/PortalContext';
import toast from '../../utils/toast';
import { motion } from 'framer-motion';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

const DatePicker = ({ selectedDate, onDateSelect, slotsData, isLoading }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dateStr = date.toISOString().split('T')[0];
      const daySlots = slotsData?.find(d => d.date === dateStr);
      const hasSlots = daySlots?.slots?.length > 0;
      const isPast = date < today;
      const isCurrentMonth = date.getMonth() === month;
      const isSelected = selectedDate === dateStr;
      
      days.push({
        date,
        dateStr,
        hasSlots,
        isPast,
        isCurrentMonth,
        isSelected
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  return (
    <Card>
      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateMonth(-1)}
            className="p-2 rounded-xl transition-colors hover:bg-white/30 dark:hover:bg-zinc-800/30"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>
          
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateMonth(1)}
            className="p-2 rounded-xl transition-colors hover:bg-white/30 dark:hover:bg-zinc-800/30"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium py-2 text-zinc-500 dark:text-zinc-400">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => !day.isPast && day.hasSlots && onDateSelect(day.dateStr)}
              disabled={day.isPast || !day.hasSlots}
              className={`relative p-2 text-sm rounded-xl transition-all duration-200 ${
                day.isSelected
                  ? 'text-white'
                  : day.isPast
                  ? 'text-zinc-500 dark:text-zinc-600'
                  : day.hasSlots
                  ? 'text-zinc-900 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30'
                  : 'text-zinc-500 dark:text-zinc-600'
              } ${!day.isCurrentMonth ? 'opacity-50' : ''}`}
              style={day.isSelected ? { backgroundColor: ACCENT } : {}}
            >
              {day.date.getDate()}
              
              {/* Availability indicator */}
              {day.hasSlots && !day.isPast && (
                <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                  day.isSelected ? 'bg-white' : 'bg-green-500'
                }`} />
              )}
            </motion.button>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center space-x-4 mt-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-zinc-600 dark:text-zinc-300">{t('booking.calendar_available')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-zinc-400"></div>
            <span className="text-zinc-600 dark:text-zinc-300">{t('booking.calendar_fully_booked')}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const SlotList = ({ selectedDate, slots, selectedSlot, onSlotSelect, isLoading, shopTimezone, unavailabilityReason }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');

  if (isLoading) {
    return (
      <Card>
        <div className="p-4 md:p-5">
          <div className="animate-pulse">
            <div className="h-6 w-32 bg-zinc-300 rounded mb-4"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-12 bg-zinc-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!selectedDate) {
    return (
      <Card>
        <div className="p-4 md:p-5">
          <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
            <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p>{t('booking.select_date_prompt')}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <Card>
        <div className="p-4 md:p-5">
          <h3 className="text-lg font-semibold mb-4 text-zinc-900 dark:text-zinc-100">
            {t('booking.available_times_header')} — {selectedDate}
          </h3>
          
          <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
            <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="font-medium mb-2">{t('booking.fully_booked_day')}</h4>
            <p className="text-sm mb-4">{t('booking.no_slots_on_date')}</p>
          
            {/* Show unavailability reason if available */}
            {unavailabilityReason && (
              <div className={`mt-4 p-4 rounded-xl ${
                unavailabilityReason.type === 'shop_day_off' 
                  ? 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
                  : unavailabilityReason.type === 'barber_day_off'
                  ? 'bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
                  : 'bg-zinc-100 border border-zinc-300 dark:bg-zinc-800/50 dark:border-zinc-600'
              }`}>
                <div className={`flex items-center justify-center mb-2 ${
                  unavailabilityReason.type === 'shop_day_off' 
                    ? 'text-red-600' 
                    : unavailabilityReason.type === 'barber_day_off'
                    ? 'text-orange-600'
                    : 'text-zinc-600'
                }`}>
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {unavailabilityReason.type === 'shop_day_off' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    ) : unavailabilityReason.type === 'barber_day_off' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  <span className="font-medium">{unavailabilityReason.message}</span>
                </div>
                {unavailabilityReason.reason && (
                  <p className={`text-sm ${
                    unavailabilityReason.type === 'shop_day_off' 
                      ? 'text-red-500' 
                      : unavailabilityReason.type === 'barber_day_off'
                      ? 'text-orange-500'
                      : 'text-zinc-500'
                  }`}>
                    Reason: {unavailabilityReason.reason}
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="block w-full px-4 py-2 rounded-xl text-sm transition-colors bg-white/50 dark:bg-zinc-800/50 hover:bg-white/70 dark:hover:bg-zinc-800/70 text-zinc-700 dark:text-zinc-300 border border-white/20 dark:border-white/10"
              >
                {t('booking.pick_another_day')}
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="block w-full px-4 py-2 rounded-xl text-sm transition-colors bg-white/50 dark:bg-zinc-800/50 hover:bg-white/70 dark:hover:bg-zinc-800/70 text-zinc-700 dark:text-zinc-300 border border-white/20 dark:border-white/10"
              >
                {t('booking.change_barber')}
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="block w-full px-4 py-2 rounded-xl text-sm transition-colors bg-white/50 dark:bg-zinc-800/50 hover:bg-white/70 dark:hover:bg-zinc-800/70 text-zinc-700 dark:text-zinc-300 border border-white/20 dark:border-white/10"
              >
                {t('booking.change_service')}
              </motion.button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Group slots by time of day
  const morningSlots = slots.filter(slot => {
    const hour = new Date(slot.local || slot.start_local).getHours();
    return hour < 12;
  });

  const afternoonSlots = slots.filter(slot => {
    const hour = new Date(slot.local || slot.start_local).getHours();
    return hour >= 12;
  });


  const formatTime = (slot) => {
    // Use the label if available, otherwise format the local time
    if (slot.label) {
      return slot.label;
    }
    const date = new Date(slot.local || slot.start_local);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <Card>
      <div className="p-4 md:p-5">
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
        {t('booking.available_times_header')} — {selectedDate}
      </h3>
      
      <div className="space-y-6">
        {/* Morning slots */}
        {morningSlots.length > 0 && (
          <div>
            <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('booking.morning')}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {morningSlots.map((slot, index) => {
                const slotTime = slot.local || slot.start_local;
                const isAvailable = slot.available !== false; // Assume available if not specified
                const isSelected = selectedSlot === slotTime;
                
                return (
                  <button
                    key={index}
                    onClick={() => isAvailable && onSlotSelect(slotTime)}
                    disabled={!isAvailable}
                    className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                      !isAvailable
                        ? 'opacity-40 cursor-not-allowed' + (isDark ? ' bg-gray-800 text-gray-500' : ' bg-gray-200 text-gray-400')
                        : isSelected
                        ? 'text-white shadow-lg'
                        : isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                    style={isSelected && isAvailable ? { 
                      background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
                      boxShadow: `0 4px 16px ${ACCENT}40`
                    } : {}}
                  >
                    <div className="flex flex-col items-center">
                      <span>{formatTime(slot)}</span>
                      {!isAvailable && (
                        <span className="text-[10px] mt-1 font-normal opacity-70">{t('booking.taken')}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Afternoon slots */}
        {afternoonSlots.length > 0 && (
          <div>
            <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('booking.afternoon')}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {afternoonSlots.map((slot, index) => {
                const slotTime = slot.local || slot.start_local;
                const isAvailable = slot.available !== false; // Assume available if not specified
                const isSelected = selectedSlot === slotTime;
                
                return (
                  <button
                    key={index}
                    onClick={() => isAvailable && onSlotSelect(slotTime)}
                    disabled={!isAvailable}
                    className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                      !isAvailable
                        ? 'opacity-40 cursor-not-allowed' + (isDark ? ' bg-gray-800 text-gray-500' : ' bg-gray-200 text-gray-400')
                        : isSelected
                        ? 'text-white shadow-lg'
                        : isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                    style={isSelected && isAvailable ? { 
                      background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
                      boxShadow: `0 4px 16px ${ACCENT}40`
                    } : {}}
                  >
                    <div className="flex flex-col items-center">
                      <span>{formatTime(slot)}</span>
                      {!isAvailable && (
                        <span className="text-[10px] mt-1 font-normal opacity-70">{t('booking.taken')}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Timezone info */}
      <div className={`mt-4 text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
        {t('booking.times_in_timezone', { timezone: shopTimezone })}
      </div>
      </div>
    </Card>
  );
};

const BookingTimePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const { draft, updateDraft } = useBookingDraft();
  const [publicBookingId, setPublicBookingId] = useState(null);
  
  // Get publicBookingId from localStorage
  useEffect(() => {
    try {
      const id = localStorage.getItem('public_booking_id');
      console.log('[BookingTimePage] Public booking ID from localStorage:', id);
      setPublicBookingId(id);
    } catch (err) {
      console.error('[BookingTimePage] Failed to read publicBookingId:', err);
      setPublicBookingId(null);
    }
  }, []);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsData, setSlotsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlots, setCurrentSlots] = useState([]);
  const [shopTimezone, setShopTimezone] = useState(() => {
    try { return localStorage.getItem('shopTimezone') || 'UTC'; } catch { return 'UTC'; }
  });
  const [barberName, setBarberName] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [serviceDuration, setServiceDuration] = useState(30);
  const [advanceWindowDays, setAdvanceWindowDays] = useState(30);
  const [currentUnavailabilityReason, setCurrentUnavailabilityReason] = useState(null);
  const [shopInfoLoaded, setShopInfoLoaded] = useState(false);

  // Redirect if no barber or service selected
  useEffect(() => {
    if (!draft.barber_user_id || !draft.service_id) {
      navigate('/app/book');
      return;
    }
  }, [draft, navigate]);

  // Load slots for the current month (only after advance window is loaded)
  useEffect(() => {
    const loadSlots = async () => {
      if (!draft.barber_user_id || !draft.service_id) return;
      
      // Don't load slots until shop info is loaded
      if (!shopInfoLoaded) {
        return;
      }
      
      // Check if we're in public booking mode
      const storedPublicId = localStorage.getItem('public_booking_id');
      if (storedPublicId && !publicBookingId) {
        // Still initializing publicBookingId state, wait for next render
        console.log('[BookingTimePage] Waiting for publicBookingId state to initialize...');
        return;
      }
      
      // Validate publicBookingId if we're in public mode
      if (storedPublicId) {
        if (!publicBookingId || publicBookingId.length < 5 || publicBookingId.includes('&')) {
          console.error('[BookingTimePage] Invalid publicBookingId:', publicBookingId);
          toast.error('Invalid booking link. Please use the correct link.');
          return;
        }
      }
      
      try {
        setIsLoading(true);
        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        
        console.log('[BookingTimePage] Loading slots with publicBookingId:', publicBookingId);
        
        const response = publicBookingId
          ? await axios.get(`/public/booking/${publicBookingId}`, {
              params: {
                barber_id: draft.barber_user_id,
                service_id: draft.service_id,
                start_date: startDate,
                days: advanceWindowDays
              }
            })
          : await axios.get('/portal/slots', {
              params: {
                barber_id: draft.barber_user_id,
                service_id: draft.service_id,
                start_date: startDate,
                days: advanceWindowDays // Load slots based on advance window
              }
            });

        const slotsResponse = publicBookingId
          ? response.data?.availability?.slots || []
          : response.data?.data?.slots || [];

        console.log('[BookingTimePage] Loaded slots for', startDate, ':', slotsResponse.find(d => d.date === startDate)?.slots);

        setSlotsData(slotsResponse);
        
        // Set default date to today if slots available, otherwise next available day
        if (slotsResponse.length > 0) {
          const todaySlots = slotsResponse.find(d => d.date === startDate);
          if (todaySlots && todaySlots.slots.length > 0) {
            setSelectedDate(startDate);
            setCurrentSlots(todaySlots.slots);
            setCurrentUnavailabilityReason(todaySlots.unavailabilityReason || null);
          } else {
            // Find next available day
            const nextAvailable = slotsResponse.find(d => d.slots.length > 0);
            if (nextAvailable) {
              setSelectedDate(nextAvailable.date);
              setCurrentSlots(nextAvailable.slots);
              setCurrentUnavailabilityReason(nextAvailable.unavailabilityReason || null);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load slots:', error);
        
        // Handle specific error cases
        if (error.response?.status === 400) {
          const errorData = error.response.data;
          if (errorData.error === 'TOO_MANY_DAYS') {
            toast.error(`Cannot load more than ${errorData.max_days} days of slots`);
          } else if (errorData.error === 'INVALID_PARAMETERS') {
            toast.error('Invalid booking parameters');
          } else {
            toast.error('Invalid request parameters');
          }
        } else {
          toast.error('Failed to load available times');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSlots();
    
    // Note: Auto-refresh removed - it was resetting date selection every 15 seconds
    // Conflict detection happens at booking time (backend validates in real-time)
    }, [draft.barber_user_id, draft.service_id, advanceWindowDays, shopInfoLoaded, publicBookingId]);

  // Load barber and service info
  useEffect(() => {
    const loadBarberServiceInfo = async () => {
      if (!draft.barber_user_id || !draft.service_id) return;
      
      // Check if we're still determining public booking mode
      const storedPublicId = localStorage.getItem('public_booking_id');
      if (storedPublicId && !publicBookingId) {
        // Still initializing publicBookingId state, wait for next render
        return;
      }
      
      try {
        if (publicBookingId) {
          console.log('[BookingTimePage] Loading shop info from public API');
          const publicResponse = await axios.get(`/public/booking/${publicBookingId}`);
          const barbers = publicResponse.data?.barbers || [];
          const services = publicResponse.data?.services || [];
          const availability = publicResponse.data?.availability || {};

          const selectedBarber = barbers.find(b => b.id === draft.barber_user_id);
          const selectedService = services.find(s => s.id === draft.service_id);

          if (selectedBarber) setBarberName(selectedBarber.name);
          if (selectedService) {
            setServiceName(selectedService.name);
            setServiceDuration(selectedService.duration_min || 30);
          }

          const tz = availability.timezone || 'UTC';
          setShopTimezone(tz);
          try { localStorage.setItem('shopTimezone', tz); } catch {}
          setAdvanceWindowDays(availability.max_advance_days || 30);
          setShopInfoLoaded(true);
        } else {
          console.log('[BookingTimePage] Loading shop info from portal API (authenticated)');
          const [barbersResponse, servicesResponse, shopResponse] = await Promise.all([
            axios.get('/portal/barbers'),
            axios.get('/portal/services'),
            axios.get('/portal/shop-info')
          ]);

          const barbers = barbersResponse.data?.barbers || [];
          const services = servicesResponse.data?.services || [];
          const selectedBarber = barbers.find(b => b.id === draft.barber_user_id);
          const selectedService = services.find(s => s.id === draft.service_id);

          if (selectedBarber) setBarberName(selectedBarber.name);
          if (selectedService) {
            setServiceName(selectedService.name);
            setServiceDuration(selectedService.duration_min || 30);
          }

          const tz = shopResponse.data.shop.timezone || 'UTC';
          setShopTimezone(tz);
          try { localStorage.setItem('shopTimezone', tz); } catch {}
          setAdvanceWindowDays(shopResponse.data.shop.advanceWindowDays || 30);
          setShopInfoLoaded(true);
        }
      } catch (error) {
        console.error('[BookingTimePage] Failed to load barber/service info:', error);
        toast.error('Failed to load booking information');
      }
    };

    loadBarberServiceInfo();
  }, [draft.barber_user_id, draft.service_id, publicBookingId]);

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    
    const daySlots = slotsData.find(d => d.date === dateStr);
    setCurrentSlots(daySlots?.slots || []);
    setCurrentUnavailabilityReason(daySlots?.unavailabilityReason || null);
  };

  const handleSlotSelect = async (slotTime) => {
    setSelectedSlot(slotTime);
    
    // Update draft with selected time (convert to format expected by backend)
    const backendTimeFormat = slotTime.split('T')[0] + 'T' + slotTime.split('T')[1].substring(0, 5);
    updateDraft({
      date: selectedDate,
      slotHHMM: slotTime.split('T')[1].substring(0, 5),
      start_local: backendTimeFormat,
      timezone: shopTimezone || 'UTC'
    });

    // Check if slot is still available (race condition protection)
    try {
      const response = publicBookingId
        ? await axios.get(`/public/booking/${publicBookingId}`, {
            params: {
              barber_id: draft.barber_user_id,
              service_id: draft.service_id,
              start_date: selectedDate,
              days: 1
            }
          })
        : await axios.get('/portal/slots', {
            params: {
              barber_id: draft.barber_user_id,
              service_id: draft.service_id,
              start_date: selectedDate,
              days: 1
            }
          });

      const slotData = publicBookingId
        ? response.data?.availability?.slots || []
        : response.data?.data?.slots || [];

      const daySlots = slotData.find(d => d.date === selectedDate);
      const matchingSlot = daySlots?.slots?.find(slot => (slot.local || slot.start_local) === slotTime);
      const slotStillAvailable = matchingSlot && (matchingSlot.available !== false);
      
      if (!slotStillAvailable) {
        setSelectedSlot(null);
        setCurrentSlots(daySlots?.slots || []);
        // Refresh the entire slots data
        const refreshResponse = publicBookingId
          ? await axios.get(`/public/booking/${publicBookingId}`, {
              params: {
                barber_id: draft.barber_user_id,
                service_id: draft.service_id,
                start_date: selectedDate,
                days: advanceWindowDays
              }
            })
          : await axios.get('/portal/slots', {
              params: {
                barber_id: draft.barber_user_id,
                service_id: draft.service_id,
                start_date: selectedDate,
                days: advanceWindowDays
              }
            });
        const refreshSlots = publicBookingId
          ? refreshResponse.data?.availability?.slots || []
          : refreshResponse.data?.data?.slots || [];
        setSlotsData(refreshSlots);
        return;
      }
    } catch (error) {
      console.error('Failed to verify slot availability:', error);
      // Don't clear selection on verification error, just log it
    }
  };

  const handleContinue = () => {
    if (!selectedSlot) return;
    
    // Ensure the draft is updated with the selected slot (convert to format expected by backend)
    const backendTimeFormat = selectedSlot.split('T')[0] + 'T' + selectedSlot.split('T')[1].substring(0, 5);
    updateDraft({
      start_local: backendTimeFormat,
      timezone: shopTimezone || 'UTC'
    });
    
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'booking_time_selected', {
        barber_id: draft.barber_user_id,
        service_id: draft.service_id,
        selected_time: selectedSlot,
        is_reschedule: !!draft.reschedule_booking_id
      });
    }
    
    navigate('/app/book/review');
  };

  const handleChangeBarber = () => {
    navigate('/app/book');
  };

  const handleChangeService = () => {
    navigate('/app/book');
  };

  const handleNextAvailable = async () => {
    try {
      setIsLoading(true);
      const today = new Date();
      let currentDate = new Date(today);
      
      // Search up to 14 days ahead
      for (let i = 0; i < 14; i++) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const daySlots = slotsData.find(d => d.date === dateStr);
        
        if (daySlots && daySlots.slots.length > 0) {
          setSelectedDate(dateStr);
          setCurrentSlots(daySlots.slots);
          setSelectedSlot(null);
          toast.success(`Found availability on ${currentDate.toLocaleDateString()}`);
          return;
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      toast.error('No availability found in the next 14 days');
    } catch (error) {
      toast.error('Failed to find next available time');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('booking.step2_title')}
        </h1>
        <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('booking.step2_subtitle')}
        </p>
        
        {/* Selection Context Chips */}
        <div className="mt-4 flex flex-wrap gap-2">
          {barberName && (
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
              isDark ? 'bg-[rgba(125,102,51,.18)] text-amber-200' : 'bg-amber-100 text-amber-800'
            }`}>
              <span className="font-medium">{t('booking.barber_label')}:</span>
              <span className="ml-1">{barberName}</span>
            </div>
          )}
          {serviceName && (
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
              isDark ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
            }`}>
              <span className="font-medium">{t('booking.service_label')}:</span>
              <span className="ml-1">{serviceName}</span>
              {serviceDuration && (
                <span className="ml-1 text-xs opacity-75">({serviceDuration} min)</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Date Picker */}
        <DatePicker
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          slotsData={slotsData}
          isLoading={isLoading}
        />

        {/* Slot List */}
        <SlotList
          selectedDate={selectedDate}
          slots={currentSlots}
          selectedSlot={selectedSlot}
          onSlotSelect={handleSlotSelect}
          isLoading={isLoading}
          shopTimezone={shopTimezone}
          unavailabilityReason={currentUnavailabilityReason}
        />
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleChangeBarber}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            {t('booking.change_barber_btn')}
          </button>
          <button
            onClick={handleChangeService}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            {t('booking.change_service_btn')}
          </button>
          <button
            onClick={handleNextAvailable}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            {t('booking.next_available')}
          </button>
        </div>

        <motion.button
          onClick={handleContinue}
          disabled={!selectedSlot}
          className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
            selectedSlot
              ? 'text-white shadow-lg'
              : 'bg-zinc-300 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400 cursor-not-allowed'
          }`}
          style={selectedSlot ? { 
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
            boxShadow: `0 4px 16px ${ACCENT}40`
          } : {}}
          whileHover={selectedSlot ? { scale: 1.02 } : {}}
          whileTap={selectedSlot ? { scale: 0.98 } : {}}
        >
          {t('booking.continue_to_review')}
        </motion.button>
      </div>
    </div>
  );
};

export default BookingTimePage;
